// RacerModal.js
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import GroupFilterDropdown from './GroupFilterDropdown';
import { useAuth } from '../contexts/AuthContext';
import { loadRacerGroupFilters, saveRacerGroupFilters } from '../utils/persistentFilters';

const RacerModal = ({ onClose, onSelectRacers, eventOwnerId, eventRacers = [], allowedRacerIds = [] }) => {
  const { user } = useAuth();
  const [racers, setRacers] = useState([]);
  const [selectedGroupFilters, setSelectedGroupFilters] = useState([]); // Array of group names
  const [distinctGroups, setDistinctGroups] = useState([]);
  const [selectedRacers, setSelectedRacers] = useState([]);
  const [filtersRestored, setFiltersRestored] = useState(false);
  const hasInitialized = useRef(false);

  // Restore persisted filters when user changes
  useLayoutEffect(() => {
    setFiltersRestored(false);
    try {
      const userId = user?.uid || null;
      const savedFilters = loadRacerGroupFilters(userId);
      setSelectedGroupFilters(savedFilters);
    } catch (error) {
      console.error("Failed to restore racer modal filters:", error);
    }
    setFiltersRestored(true);
  }, [user?.uid]);

  // Persist filters when they change
  useEffect(() => {
    if (!filtersRestored) return;
    try {
      const userId = user?.uid || null;
      saveRacerGroupFilters(userId, selectedGroupFilters);
    } catch (error) {
      console.error("Failed to persist racer modal filters:", error);
    }
  }, [selectedGroupFilters, user?.uid, filtersRestored]);

  // Fetch racers and compute distinct groups on mount
  useEffect(() => {
    // Only initialize once to prevent re-renders from resetting selections
    if (hasInitialized.current) return;
    
    const fetchRacers = async () => {
      const racersRef = collection(db, "timer_racers");
      const racerList = [];

      if (Array.isArray(allowedRacerIds) && allowedRacerIds.length > 0) {
        const allowedSet = new Set(allowedRacerIds);
        const querySnapshot = await getDocs(racersRef);
        querySnapshot.forEach(docSnap => {
          if (allowedSet.has(docSnap.id)) {
            racerList.push({ id: docSnap.id, ...docSnap.data() });
          }
        });
      } else if (eventOwnerId) {
        // Filter racers to only those created by the event owner.
        const q = query(racersRef, where("createdBy", "==", eventOwnerId));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach(docSnap => {
          racerList.push({ id: docSnap.id, ...docSnap.data() });
        });
      } else {
        setRacers([]);
        setSelectedRacers([]);
        setDistinctGroups([]);
        hasInitialized.current = true;
        return;
      }

      const sortedRacers = racerList.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
      setRacers(sortedRacers);
      
      // Pre-select racers that are already part of the event
      const eventRacerIds = eventRacers.map(er => er.racerId);
      const preSelectedRacers = sortedRacers.filter(racer => 
        eventRacerIds.includes(racer.id)
      );
      setSelectedRacers(preSelectedRacers);
      
      // Compute distinct groups
      const groupsSet = new Set();
      racerList.forEach(racer => {
        if (racer.groups) {
          racer.groups.forEach(g => groupsSet.add(g));
        }
      });
      setDistinctGroups(Array.from(groupsSet).sort());
      hasInitialized.current = true;
    };
    fetchRacers();
  }, [allowedRacerIds, eventOwnerId]); // Removed eventRacers from dependencies

  // Toggle group filter
  const toggleGroupFilter = (groupName) => {
    setSelectedGroupFilters(prev => {
      if (prev.includes(groupName)) {
        return prev.filter(g => g !== groupName);
      } else {
        return [...prev, groupName];
      }
    });
  };

  // Clear all group filters
  const clearGroupFilters = () => {
    setSelectedGroupFilters([]);
  };

  // Filter racers based on the selected groups
  const filteredRacers = racers.filter(racer => {
    if (selectedGroupFilters.length === 0) return true;
    return racer.groups && racer.groups.some(group => selectedGroupFilters.includes(group));
  });

  const toggleSelectRacer = (racer) => {
    const isSelected = selectedRacers.some(r => r.id === racer.id);
    if (isSelected) {
      setSelectedRacers(selectedRacers.filter(r => r.id !== racer.id));
    } else {
      setSelectedRacers([...selectedRacers, racer]);
    }
  };

  const isFilteredAllSelected = filteredRacers.length > 0
    ? filteredRacers.every(racer => selectedRacers.some(r => r.id === racer.id))
    : false;

  const toggleSelectAllFiltered = () => {
    if (filteredRacers.length === 0) return;
    if (isFilteredAllSelected) {
      const filteredIds = new Set(filteredRacers.map(r => r.id));
      setSelectedRacers(selectedRacers.filter(r => !filteredIds.has(r.id)));
    } else {
      const selectedMap = new Map(selectedRacers.map(r => [r.id, r]));
      filteredRacers.forEach(racer => {
        if (!selectedMap.has(racer.id)) {
          selectedMap.set(racer.id, racer);
        }
      });
      setSelectedRacers(Array.from(selectedMap.values()));
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "0",
        margin: "0",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          padding: "20px",
          borderRadius: "8px",
          width: "90%",
          maxWidth: "600px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "none",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            color: "#666",
            zIndex: 10,
            padding: "5px",
            lineHeight: 1,
          }}
          title="Close"
        >
          ×
        </button>

        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          marginBottom: "15px",
          paddingRight: "40px" // Add space for close button
        }}>
          <h3 style={{ margin: 0 }}>Select Racers</h3>
          <div style={{
            color: "#666",
            fontSize: "14px",
            fontWeight: "normal"
          }}>
            {selectedRacers.length} selected
          </div>
        </div>
        
        {/* Group filter dropdown */}
        <div style={{ marginBottom: "15px" }}>
          <GroupFilterDropdown
            distinctGroups={distinctGroups}
            selectedGroupFilters={selectedGroupFilters}
            onToggleGroupFilter={toggleGroupFilter}
            onClearGroupFilters={clearGroupFilters}
            label="Filter by group:"
          />
        </div>
        
        {/* Scrollable racers table container */}
        <div style={{ 
          flex: 1, 
          overflowY: "auto", 
          minHeight: 0,
          marginBottom: "15px"
        }}>
          <table style={{ 
            width: "100%", 
            borderCollapse: "collapse",
            minWidth: "100%"
          }}>
            <thead style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
              <tr style={{ borderBottom: "1px solid #ccc" }}>
                <th style={{ textAlign: "left", padding: "8px" }}>Name</th>
                <th style={{ textAlign: "left", padding: "8px" }}>Group</th>
                <th style={{ padding: "8px", textAlign: "center" }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <input
                      type="checkbox"
                      checked={isFilteredAllSelected}
                      onChange={toggleSelectAllFiltered}
                      disabled={filteredRacers.length === 0}
                      aria-label="Select all filtered racers"
                    />
                    Select All
                  </label>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRacers.map((racer) => (
                <tr key={racer.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "8px" }}>{racer.name}</td>
                  <td style={{ padding: "8px" }}>
                    {racer.groups && racer.groups.length > 0
                      ? racer.groups.join(", ")
                      : "No group"}
                  </td>
                  <td style={{ padding: "8px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={!!selectedRacers.find(r => r.id === racer.id)}
                      onChange={() => toggleSelectRacer(racer)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Action buttons - fixed at bottom */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between",
          borderTop: "1px solid #eee",
          paddingTop: "15px",
          flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 15px",
              backgroundColor: "#ccc",
              border: "none",
              color: "#333",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSelectRacers(selectedRacers)}
            style={{
              padding: "10px 15px",
              backgroundColor: "#13A1A8",
              border: "none",
              color: "#fff",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Add Selected Racers
          </button>
        </div>
      </div>
    </div>
  );
};

export default RacerModal;